import type { IBankAccount } from "./IBankAccount";
import type { ICarbucksProfile, ICarbucksProfileDto } from "./ICarbucksProfile";
import type { ILinkedCard } from "./ILinkedCard";
import type { ITransaction } from "./ITransaction";
import { LinkedCard } from "./LinkedCard";
import { Transaction } from "./Transaction";
import { useUserSettings } from "@services/UserSettings";
import { useRoute } from "vue-router";
import { isDemo, isNotProduction } from "@utilities/Environments";
import { useMockAvailableAmount, useMockBankAccount, useMockLifetimeAmount, useMockLinkedCards, useMockPendingTransactions } from "./_MockData";

export class CarbucksProfile implements ICarbucksProfile
{

    private readonly LINK_CARD_URL_TEMPLATE = import.meta.env.VITE_LINK_CARD_URL as string;

    userId?: number;
    userToken?: string;
    availableAmount: number;
    lifetimeAmount: number;
    pendingAmount: number;
    linkedCards: ILinkedCard[];
    pendingTransactions: ITransaction[];
    bankAccount: IBankAccount;
    isLoaded: boolean;

    readonly MinimumCarbucksBeforeRedemption = 30;

    /**
     * Gets whether bank account details exist against the profile.
     * @returns True if both a BSB and Account Number are present.
     */
    get hasBankAccountDetails(): boolean
    {
        return !!(this.bankAccount?.bsb) && !!(this.bankAccount?.accountNumber);
    }

    /**
     * Gets whether the user has enough funds to be able to redeem their cashback.
     * @returns True if the available amount is greater than or equal to 30.
     */
    get hasCashbackThatCanBeRedeemed(): boolean
    {
        return this.canRedeemCashbackAmount( this.MinimumCarbucksBeforeRedemption );
    }

    constructor( model: ICarbucksProfileDto )
    {

        // Only create mock data for the carbucks profile if we're in
        // a development environment
        if( isNotProduction() )
        {
            this.createMockData( model );
        }

        this.userId = model.userId;
        this.userToken = model.userToken;
        this.linkedCards = model.linkedCards.map( c => new LinkedCard( c ) );
        this.pendingTransactions = model.pendingTransactions.map( t => new Transaction( t ) );
        this.pendingAmount = this.pendingTransactions.reduce( ( total, t ) => total + ( t.cashbackAmount ?? 0 ), 0 );
        this.availableAmount = model.availableAmount;
        this.lifetimeAmount = model.lifetimeAmount;
        this.bankAccount = model.bankAccount;
        this.isLoaded = true;
        
    }

    /**
     * Gets the outbound url for linking a card via the offers program.  The URL will be retrieved
     * containing the user token pass through and the return URL to the current environment
     * @returns 
     */
    getLinkCardUrl( returnPath?: string )
    {
        const route = useRoute();
        const host = window.location.protocol + "//" + window.location.host;
        var urlTemplate = this.LINK_CARD_URL_TEMPLATE;
        urlTemplate = urlTemplate.replace( '{userToken}', encodeURIComponent( this.userToken?.toString() ?? '' ) );
        urlTemplate = urlTemplate.replace( '{redirectURL}', encodeURIComponent( host + ( returnPath ?? route.fullPath ) ) );
        return urlTemplate;
    }

    /**
     * True if the user has linked cards and the user setting to disable the linked cards has not been set
     * @returns 
     */
    get hasLinkedCards()
    {
        return this.linkedCards.length > 0;
    }

    /**
     * Updates the (masked) bank account details on the current profile.
     * @param bsb The new BSB
     * @param accountNumber The new account number
     */
    updateBankAccount( bsb?: string, accountNumber?: string ): void
    {
        this.bankAccount = {
            bsb,
            accountNumber
        };
    }

    /**
     * Updates the available amount for the current profile.
     * @param availableAmount The new available amount.
     */
    updateAvailableAmount( availableAmount: number ): void
    {
        this.availableAmount = availableAmount;
    }

    /**
     * Gets whether the user has enough cashback to be able to redeem a specific amount.
     * @param amount The amount that the user wants to withdraw.
     * @returns True if the available amount is greater than or equal to the amount provided.
     */
    canRedeemCashbackAmount( amount: number ): boolean
    {
        return this.availableAmount >= amount;
    }

    /**
     * TEMP FUNCTION TO CREATE MOCK DATA FOR DEMO
     * @param model 
     */
    private createMockData( model: ICarbucksProfileDto )
    {

        const userSettings = useUserSettings();

        if( isDemo() || userSettings.enableDemoBankAccount )
        {
            model.bankAccount = useMockBankAccount();
        }

        if( isDemo() || userSettings.enableDemoTransactions )
        {
            model.availableAmount = useMockAvailableAmount();
            model.lifetimeAmount = useMockLifetimeAmount();
            model.pendingTransactions = useMockPendingTransactions();
        }

        if( isDemo() || userSettings.enableDemoLinkedCards )
        {
            model.linkedCards = useMockLinkedCards();
        }
    
    }

}